'use client';

import { Check } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import {
  ENTERPRISE_PLAN_DISPLAY,
  INDIVIDUAL_PLAN_DISPLAY,
  formatEnterprisePrice,
} from '@/lib/billing/pricing-display';

type Cadence = 'monthly' | 'annual';

interface PricingTableProps {
  onSelectPlan: (planCode: string, cadence: Cadence) => void;
  isAuthenticated: boolean;
  isLoading?: boolean;
  currentPlanCode?: 'individual' | 'enterprise_1' | 'enterprise_5' | 'pay_as_you_go' | null;
}

function CadenceToggle({
  cadence,
  onChange,
}: {
  cadence: Cadence;
  onChange: (c: Cadence) => void;
}) {
  const t = useTranslations('landing.pricing.table');
  return (
    <div className="flex items-center justify-center gap-4 bg-white/[0.03] border border-white/10 rounded-full p-1.5 w-fit mx-auto mb-12">
      <button
        onClick={() => onChange('monthly')}
        className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
          cadence === 'monthly'
            ? 'bg-white/10 text-white shadow-lg'
            : 'text-white/50 hover:text-white/80'
        }`}
      >
        {t('monthly')}
      </button>
      <button
        onClick={() => onChange('annual')}
        className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
          cadence === 'annual'
            ? 'bg-white/10 text-white shadow-lg'
            : 'text-white/50 hover:text-white/80'
        }`}
      >
        {t('annual')}
        <span className="text-[10px] uppercase tracking-wider bg-primary/20 text-primary px-2 py-0.5 rounded-full">
          {t('savePercent')}
        </span>
      </button>
    </div>
  );
}

const enterprisePlans = ENTERPRISE_PLAN_DISPLAY;

export function PricingTable({
  onSelectPlan,
  isAuthenticated,
  isLoading,
  currentPlanCode = null,
}: PricingTableProps) {
  const t = useTranslations('landing.pricing');
  const tt = useTranslations('landing.pricing.table');

  const planNameKey: Record<string, string> = {
    enterprise_1: 'planEnterprise1Name',
    enterprise_5: 'planEnterprise5Name',
    pay_as_you_go: 'planPaygName',
  };
  const planDescKey: Record<string, string> = {
    enterprise_1: 'planEnterprise1Desc',
    enterprise_5: 'planEnterprise5Desc',
    pay_as_you_go: 'planPaygDesc',
  };

  const getPrice = (planCode: (typeof enterprisePlans)[0]['code']) =>
    formatEnterprisePrice(planCode).replace('/mo', '');

  const getCtaText = (planCode: string) => {
    if (isAuthenticated && currentPlanCode === planCode) return tt('currentPlan');
    if (isAuthenticated) return tt('selectPlan');
    return tt('getStarted');
  };

  // Translate feature strings: exact match for static ones, regex for dynamic patterns
  const translateFeature = (feature: string) => {
    const featureMap: Record<string, string> = {
      'Team analytics': t('featureTeamAnalytics'),
      'Custom requirements': t('featureCustomRequirements'),
      'Priority support': t('featurePrioritySupport'),
      'Unlimited interview minutes': t('featureUnlimitedMinutes'),
      'Interview recordings': t('featureRecordings'),
      'Candidate scorecards': t('featureScorecards'),
      'AI feedback and analytics': t('featureAiFeedback'),
      'Performance tracking': t('featurePerfTracking'),
      'ATS tracking system': t('featureAtsTracking'),
    };
    if (featureMap[feature]) return featureMap[feature];

    // "1,200 minutes / month" pattern
    const minutesMatch = feature.match(/^([\d,]+)\s*minutes\s*\/\s*month$/i);
    if (minutesMatch) return t('featureMinutesMonth', { minutes: minutesMatch[1] });

    // "Overage at $0.05/min" pattern
    const overageMatch = feature.match(/^Overage at \$([\d.]+)\/min$/i);
    if (overageMatch) return t('featureOverage', { rate: overageMatch[1] });

    return feature;
  };

  const isCurrentPlan = (planCode: string) => isAuthenticated && currentPlanCode === planCode;

  return (
    <div className="space-y-24">
      {/* ── Enterprise Cards ── */}
      <div>
        <div className="text-center mb-12">
          <h2 className="text-3xl font-serif text-white mb-4">{t('enterprise.title')}</h2>
          <p className="text-white/50 max-w-xl mx-auto">{t('enterprise.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-[1440px] mx-auto">
          {enterprisePlans.map((plan) => (
            <motion.div
              key={plan.code}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`relative flex flex-col p-10 rounded-3xl border transition-colors ${
                plan.popular
                  ? 'bg-white/[0.04] border-primary/30 shadow-[0_0_40px_rgba(255,108,92,0.1)]'
                  : 'bg-black/50 border-white/10 hover:bg-white/[0.02]'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider py-1 px-4 rounded-full">
                  {tt('mostPopular')}
                </div>
              )}

              <div className="mb-8">
                <h3 className="text-xl font-medium text-white mb-2">
                  {tt(planNameKey[plan.code] ?? plan.name)}
                </h3>
                <p className="text-sm text-white/50">
                  {tt(planDescKey[plan.code] ?? plan.description)}
                </p>
              </div>

              <div className="mb-8 flex items-end gap-2">
                <span className="text-5xl font-serif text-white">{getPrice(plan.code)}</span>
                {!plan.isMetered && <span className="text-white/50 mb-2">{tt('perMo')}</span>}
              </div>

              <button
                onClick={() => onSelectPlan(plan.code, 'monthly')}
                disabled={isLoading || isCurrentPlan(plan.code)}
                className={`w-full rounded-full py-3 text-sm font-medium transition-all mb-8 ${
                  plan.popular
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105'
                    : 'bg-white/10 text-white hover:bg-white/20'
                } disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed`}
              >
                {isLoading ? tt('loading') : getCtaText(plan.code)}
              </button>

              <div className="space-y-4 mt-auto">
                <p className="text-sm font-medium text-white/80">{tt('includes')}</p>
                {plan.cardFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm text-white/60">{translateFeature(feature)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Candidate Card ── */}
      <div className="max-w-4xl mx-auto">
        <div className="rounded-[2.5rem] bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-white/10 p-8 md:p-12 flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1">
            <h2 className="text-2xl font-serif text-white mb-2">{tt('forCandidates')}</h2>
            <p className="text-white/60 mb-6">{tt('individualDesc')}</p>
            <div className="flex items-center gap-4 mb-8">
              <span className="text-4xl font-serif text-white">
                ${INDIVIDUAL_PLAN_DISPLAY.monthlyPrice}
              </span>
              <span className="text-white/50">{tt('perMonth')}</span>
            </div>
            <button
              onClick={() => onSelectPlan('individual', 'monthly')}
              disabled={isLoading || isCurrentPlan('individual')}
              className="px-8 py-3 rounded-full bg-primary text-white hover:brightness-110 hover:scale-105 transition-all duration-300 font-medium disabled:opacity-50 disabled:hover:scale-100"
            >
              {isLoading ? tt('loading') : getCtaText('individual')}
            </button>
          </div>

          <div className="flex-1 w-full space-y-4 bg-black/40 rounded-2xl p-6 border border-white/5">
            {INDIVIDUAL_PLAN_DISPLAY.cardFeatures.map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-primary" />
                <span className="text-sm text-white/70">{translateFeature(feature)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
